import React from 'react'
import axios from 'axios'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon
} from '@material-ui/icons'

const emptyRate = { scope: '', description: '', role: '', unit: '', baseRate: '', notes: '' }

const ProductionRatesTable = ({ url }) => {
  const [rates, setRates] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [editingId, setEditingId] = React.useState(null)
  const [editData, setEditData] = React.useState({})
  const [adding, setAdding] = React.useState(false)
  const [newRate, setNewRate] = React.useState({ ...emptyRate })
  const [scopeFilter, setScopeFilter] = React.useState('')
  const [deleteTarget, setDeleteTarget] = React.useState(null)

  React.useEffect(() => {
    fetchRates()
  }, [])

  const fetchRates = () => {
    setLoading(true)
    axios.get(`${url}/api/bid-estimation/production-rates`).then(response => {
      setRates(response.data)
      setLoading(false)
    }).catch(err => {
      console.error(err)
      setLoading(false)
    })
  }

  const scopes = React.useMemo(() => {
    const unique = [...new Set(rates.map(r => r.scope).filter(Boolean))]
    return unique.sort()
  }, [rates])

  const filteredRates = scopeFilter
    ? rates.filter(r => r.scope === scopeFilter)
    : rates

  const handleEdit = (rate) => {
    setEditingId(rate._id)
    setEditData({
      scope: rate.scope || '',
      description: rate.description || '',
      role: rate.role || '',
      unit: rate.unit || '',
      baseRate: rate.baseRate ?? '',
      notes: rate.notes || ''
    })
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditData({})
  }

  const handleSaveEdit = () => {
    const payload = {
      ...editData,
      baseRate: Number(editData.baseRate)
    }
    axios.put(`${url}/api/bid-estimation/production-rates/${editingId}`, payload).then(() => {
      setEditingId(null)
      setEditData({})
      fetchRates()
    }).catch(err => {
      console.error(err)
    })
  }

  const handleAdd = () => {
    const payload = {
      ...newRate,
      baseRate: Number(newRate.baseRate)
    }
    axios.post(`${url}/api/bid-estimation/production-rates`, payload).then(() => {
      setAdding(false)
      setNewRate({ ...emptyRate })
      fetchRates()
    }).catch(err => {
      console.error(err)
    })
  }

  const handleDelete = () => {
    if (!deleteTarget) return
    axios.delete(`${url}/api/bid-estimation/production-rates/${deleteTarget._id}`).then(() => {
      setDeleteTarget(null)
      fetchRates()
    }).catch(err => {
      console.error(err)
      setDeleteTarget(null)
    })
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <CircularProgress />
      </div>
    )
  }

  return (
    <div>
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
        <Grid item>
          <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
            <InputLabel>Filter by Scope</InputLabel>
            <Select
              value={scopeFilter}
              onChange={e => setScopeFilter(e.target.value)}
              label="Filter by Scope"
            >
              <MenuItem value="">All Scopes</MenuItem>
              {scopes.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item style={{ marginLeft: 'auto' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setAdding(true)}
            disabled={adding}
          >
            Add Rate
          </Button>
        </Grid>
      </Grid>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Scope</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Unit</TableCell>
            <TableCell align="right">Base Rate</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {adding && (
            <AddRow
              data={newRate}
              onChange={setNewRate}
              onSave={handleAdd}
              onCancel={() => { setAdding(false); setNewRate({ ...emptyRate }) }}
            />
          )}
          {filteredRates.length === 0 && !adding ? (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography variant="body2" style={{ padding: 20 }}>
                  No production rates found.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredRates.map(rate => (
              <TableRow key={rate._id}>
                <TableCell>
                  {editingId === rate._id ? (
                    <TextField size="small" value={editData.scope} onChange={e => setEditData({ ...editData, scope: e.target.value })} />
                  ) : rate.scope}
                </TableCell>
                <TableCell>
                  {editingId === rate._id ? (
                    <TextField size="small" value={editData.description} onChange={e => setEditData({ ...editData, description: e.target.value })} />
                  ) : rate.description}
                </TableCell>
                <TableCell>
                  {editingId === rate._id ? (
                    <TextField size="small" value={editData.role} onChange={e => setEditData({ ...editData, role: e.target.value })} />
                  ) : rate.role}
                </TableCell>
                <TableCell>
                  {editingId === rate._id ? (
                    <TextField size="small" value={editData.unit} onChange={e => setEditData({ ...editData, unit: e.target.value })} />
                  ) : rate.unit}
                </TableCell>
                <TableCell align="right">
                  {editingId === rate._id ? (
                    <TextField size="small" type="number" value={editData.baseRate} onChange={e => setEditData({ ...editData, baseRate: e.target.value })} style={{ width: 80 }} />
                  ) : (
                    `$${Number(rate.baseRate || 0).toFixed(2)}`
                  )}
                </TableCell>
                <TableCell>
                  {editingId === rate._id ? (
                    <TextField size="small" value={editData.notes} onChange={e => setEditData({ ...editData, notes: e.target.value })} />
                  ) : rate.notes}
                </TableCell>
                <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                  {editingId === rate._id ? (
                    <>
                      <IconButton size="small" onClick={handleSaveEdit}><SaveIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={handleCancelEdit}><CancelIcon fontSize="small" /></IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton size="small" onClick={() => handleEdit(rate)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => setDeleteTarget(rate)}><DeleteIcon fontSize="small" /></IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Production Rate</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteTarget?.description}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const AddRow = ({ data, onChange, onSave, onCancel }) => (
  <TableRow>
    <TableCell>
      <TextField size="small" placeholder="Scope" value={data.scope} onChange={e => onChange({ ...data, scope: e.target.value })} />
    </TableCell>
    <TableCell>
      <TextField size="small" placeholder="Description" value={data.description} onChange={e => onChange({ ...data, description: e.target.value })} />
    </TableCell>
    <TableCell>
      <TextField size="small" placeholder="Role" value={data.role} onChange={e => onChange({ ...data, role: e.target.value })} />
    </TableCell>
    <TableCell>
      <TextField size="small" placeholder="Unit" value={data.unit} onChange={e => onChange({ ...data, unit: e.target.value })} />
    </TableCell>
    <TableCell align="right">
      <TextField size="small" type="number" placeholder="Rate" value={data.baseRate} onChange={e => onChange({ ...data, baseRate: e.target.value })} style={{ width: 80 }} />
    </TableCell>
    <TableCell>
      <TextField size="small" placeholder="Notes" value={data.notes} onChange={e => onChange({ ...data, notes: e.target.value })} />
    </TableCell>
    <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
      <IconButton size="small" onClick={onSave}><SaveIcon fontSize="small" /></IconButton>
      <IconButton size="small" onClick={onCancel}><CancelIcon fontSize="small" /></IconButton>
    </TableCell>
  </TableRow>
)

export default ProductionRatesTable
